/**
 * Helpers d'échéances déclaratives (Lot F) — logique de RAPPEL, pure (sans I/O).
 *
 * Ces fonctions calculent les dates d'échéance AFFICHÉES (bandeau du hub, widget
 * dashboard) et la saisonnalité des cartes DR/DS. Ce sont des RAPPELS : la date
 * opposable et le calcul réglementaire réel (report jour ouvré, arrêté
 * préfectoral) vivent côté serveur, dans les snapshots DRM/DR/DS. On ne prend
 * AUCUNE décision de conformité ici — juste « pour quand dois-je m'en occuper ? ».
 *
 * Règles de rappel (UX §10.3 + mémo réglementaire) :
 *   - DRM : mensuelle, à déposer avant le 10 du mois suivant.
 *   - DS  : stock au 31/07, à déposer avant le 10/09 (ouverte dès le 1er août).
 *   - DR  : annuelle, disponible dès la vendange (octobre), ~10 décembre.
 */

import type { BadgeVariant } from "@champ-erp/ui";

/** Noms de mois FR — l'index 1 vaut « janvier » (le [0] est un placeholder). */
const MOIS_FR = [
  "",
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

/** Libellé français d'un mois 1..12 (ex : 6 → « juin »). */
export function libelleMois(mois: number): string {
  return MOIS_FR[mois] ?? String(mois);
}

/** Période mensuelle (mois 1..12 + année) — clé d'une DRM. */
export interface PeriodeMensuelle {
  annee: number;
  mois: number;
}

/** Construit une date ISO `YYYY-MM-DD` à partir de composants numériques. */
function isoDate(annee: number, mois: number, jour: number): string {
  return `${annee}-${String(mois).padStart(2, "0")}-${String(jour).padStart(2, "0")}`;
}

/**
 * Période à déclarer par défaut : le mois PRÉCÉDENT (on déclare juin en juillet).
 * C'est l'obligation DRM courante à un instant donné.
 */
export function periodeADeclarer(today: Date): PeriodeMensuelle {
  const mois = today.getMonth() + 1;
  const annee = today.getFullYear();
  // Janvier → on déclare décembre de l'année précédente.
  if (mois === 1) return { annee: annee - 1, mois: 12 };
  return { annee, mois: mois - 1 };
}

/** Échéance d'une DRM : le 10 du mois SUIVANT la période déclarée. */
export function echeanceDrm(periode: PeriodeMensuelle): string {
  const moisSuivant = periode.mois === 12 ? 1 : periode.mois + 1;
  const anneeSuivant = periode.mois === 12 ? periode.annee + 1 : periode.annee;
  return isoDate(anneeSuivant, moisSuivant, 10);
}

/** Échéance de la DS d'une année (stock au 31/07) : le 10 septembre. */
export function echeanceDs(annee: number): string {
  return isoDate(annee, 9, 10);
}

/** Échéance indicative de la DR d'un millésime : ~10 décembre (arrêté préfectoral). */
export function echeanceDr(annee: number): string {
  return isoDate(annee, 12, 10);
}

/** La DS est « ouverte » du 1er août au 10 septembre (sinon carte grisée). */
export function saisonDsOuverte(today: Date): boolean {
  const mois = today.getMonth() + 1;
  return mois === 8 || mois === 9;
}

/** La DR est « ouverte » dès la vendange (octobre → décembre). */
export function saisonDrOuverte(today: Date): boolean {
  return today.getMonth() + 1 >= 10;
}

const MS_PAR_JOUR = 24 * 60 * 60 * 1000;

/**
 * Nombre de jours entiers entre aujourd'hui et une échéance ISO (positif = à
 * venir, négatif = dépassée). On compare des dates « pures » (minuit local) pour
 * qu'une différence d'heures ne fasse pas basculer le compteur.
 */
export function joursRestants(echeanceIso: string, today: Date): number {
  const [annee, mois, jour] = echeanceIso.split("-").map(Number);
  const cible = new Date(annee!, mois! - 1, jour!).getTime();
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.round((cible - base) / MS_PAR_JOUR);
}

/** Libellé court d'un compte à rebours (« J−3 », « aujourd'hui », « en retard »). */
export function formatJoursRestants(jours: number): string {
  if (jours < 0) return "en retard";
  if (jours === 0) return "aujourd'hui";
  return `J−${jours}`;
}

/** Urgent dès J−7 (fond rust de la DatePill) — et a fortiori si dépassé. */
export function echeanceUrgente(jours: number): boolean {
  return jours <= 7;
}

// ────────────────────────────────────────────────────────────────────────────
// État d'une déclaration (cycle de vie affiché sur les cartes / le widget)
// ────────────────────────────────────────────────────────────────────────────

/** État d'avancement d'une déclaration pour l'affichage. */
export type EtatDeclaration = "absente" | "brouillon" | "transmise" | "neant";

/** Statut brut renvoyé par les routers (brouillon / transmise) — ou absent. */
type StatutBrut = "brouillon" | "transmise" | null | undefined;

/** Déduit l'état d'affichage depuis le statut brut + le flag néant. */
export function etatDepuisStatut(statut: StatutBrut, estNeant: boolean): EtatDeclaration {
  if (statut === "transmise") return estNeant ? "neant" : "transmise";
  if (statut === "brouillon") return "brouillon";
  return "absente";
}

/** Une déclaration transmise (ou néant transmise) est « faite » — plus urgente. */
export function estFaite(etat: EtatDeclaration): boolean {
  return etat === "transmise" || etat === "neant";
}

/** Libellé du CTA adapté à l'état (« Préparer » / « Reprendre » / « Voir »). */
export function ctaSelonEtat(etat: EtatDeclaration): string {
  if (etat === "brouillon") return "Reprendre";
  if (etat === "absente") return "Préparer";
  return "Voir";
}

/** Config d'affichage d'un état : libellé + variante Badge + verrou (transmise). */
export const ETAT_DECLARATION_CONFIG: Record<
  EtatDeclaration,
  { label: string; variant: BadgeVariant; verrouille: boolean }
> = {
  absente: { label: "À faire", variant: "neutral", verrouille: false },
  brouillon: { label: "Brouillon", variant: "warning", verrouille: false },
  transmise: { label: "Transmise", variant: "success", verrouille: true },
  neant: { label: "Néant transmise", variant: "neutral", verrouille: true },
};
